import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Rating } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import moment from 'moment';
import { SimpleModal } from 'components/Modal';
import { setChatbaseUiHidden } from 'components/ChatWidget';
import FutureCollaboration from './components/FutureCollaboration';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { exitReact } from 'utils';
import { JobProps, HygienistProps } from 'utils/types';
import ApiError from 'api/ApiError';
import api from 'api';
import PositiveFeedbackModal from './components/PositiveFeedbackModal';
import NegativeFeedbackModal from './components/NegativeFeedbackModal';
import PositiveSuccessModal from './components/PositiveSuccessModal';
import NegativeSuccessModal from './components/NegativeSuccessModal';
import ReviewAskModal, { ReviewAskPayload } from 'components/ReviewAskModal';
import useActionApi from 'hooks/useActionApi';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError/FixedError';
import styled, { createGlobalStyle } from 'styled-components';

import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { isJobStartDateEligibleForFeedbackSurveyPopup } from '../../../utils/surveyPopupEligibility';
import {
  canCompleteDentistSurvey,
  resolveFeedbackViewOnlyMode,
} from '../../../utils/feedbackEditEligibility';

type CollabValue = 'favourite' | 'neutral' | 'block';

type RatingFieldKey = 'clinical' | 'professional' | 'communication' | 'time' | 'value';
const modalFont = "var(--fontMain, 'Poppins', sans-serif)";
const SURVEY_RESHOW_MS = 24 * 60 * 60 * 1000;

const normalizeFlag = (value?: boolean | number | string | null) =>
  value === true || value === 1 || value === '1';

const normalizeRating = (value?: number | string | null) => {
  const rating = Number(value);
  return Number.isFinite(rating) ? rating : 0;
};

type FeedbackAwareJob = Omit<Partial<JobProps>, 'hygienistRating' | 'feedbackSubmitted' | 'surveyDismissed' | 'dentistSurveySkipCount'> & {
  hygienistRating?: number | string | null;
  feedbackSubmitted?: boolean | number | string | null;
  surveyDismissed?: boolean | number | string | null;
  dentistSurveySkipCount?: number | string | null;
  dentistSurveySkippedAt?: string | null;
  hygienist?: {
    firstName?: string | null;
    lastName?: string | null;
  };
};

type DentistSurveyJob = {
  job: JobProps;
  hygienist: HygienistProps;
  locationId: string | number;
};

export const computeHasFeedback = (jobLike: FeedbackAwareJob) => {
  const rating = jobLike.hygienistRating;
  const submitted = jobLike.feedbackSubmitted;
  const dismissed = jobLike.surveyDismissed;
  return normalizeFlag(submitted) || normalizeFlag(dismissed) || normalizeRating(rating) > 0;
};

const getDentistSurveyDisplayTime = (jobLike: FeedbackAwareJob) => {
  const rawValue = jobLike?.shifts?.[0]?.shiftDate
    || jobLike?.startDate;
  const time = rawValue ? new Date(rawValue).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

export const isDentistSurveyEligible = (jobLike: FeedbackAwareJob) => {
  const skipCount = Number(jobLike.dentistSurveySkipCount || 0);
  if (skipCount >= 2) return false;
  if (!skipCount) return true;

  const skippedAt = jobLike.dentistSurveySkippedAt ? new Date(jobLike.dentistSurveySkippedAt).getTime() : NaN;
  return Number.isFinite(skippedAt) && (Date.now() - skippedAt >= SURVEY_RESHOW_MS);
};

export const buildDentistSurveyQueue = (jobs: FeedbackAwareJob[]): DentistSurveyJob[] => (
  (jobs || [])
    .filter((jobLike) => {
      const hasHyg = !!jobLike.hygienistId;
      const isCompleted = Number(jobLike.status) === 4;
      return hasHyg && isCompleted && !computeHasFeedback(jobLike) && isDentistSurveyEligible(jobLike)
        && isJobStartDateEligibleForFeedbackSurveyPopup(jobLike.startDate as unknown as string | Date | null | undefined);
    })
    .sort((a, b) => getDentistSurveyDisplayTime(a) - getDentistSurveyDisplayTime(b))
    .filter((jobLike) => !!jobLike.hygienist?.firstName)
    .map((jobLike) => ({
      job: jobLike as JobProps,
      hygienist: {
        id: Number(jobLike.hygienistId),
        firstName: String(jobLike.hygienist?.firstName || ''),
        lastName: String(jobLike.hygienist?.lastName || ''),
      } as HygienistProps,
      locationId: jobLike.locationId || '',
    }))
);

export const getDentistFeedbackFollowUpType = (
  collaborationPreference?: 'favourite' | 'block' | 'neutral' | null,
) => {
  if (collaborationPreference === 'favourite') return 'positive';
  if (collaborationPreference === 'block') return 'negative';
  return 'none';
};

// Widen the SimpleModal window without breaking its internals
const WiderModalStyle = createGlobalStyle`
  .feedback-do-modal [class*="window"] {
    max-width: 42rem !important;
    padding: 0 !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  color: #202939;
  font-family: ${modalFont};
`;

const ModalHeaderBand = styled.div`
  background: #f5f7fa;
  padding: 10px 20px 8px;
  border-bottom: 1px solid #edf0f5;
`;

const ModalHeaderInner = styled.div`
  display: flex; align-items: center; justify-content: flex-start;
`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const HeaderTitle = styled.span`
  color: #586174;
  font-size: 0.88rem;
  font-weight: 400;
  font-family: ${modalFont};
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px 20px 20px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
`;
const QuestionTitle = styled.h2`
  margin: 0 0 14px 0;
  color: #1d2433;
  font-size: 1.38rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;
const StarRow = styled.div`display: flex; gap: 10px; margin-bottom: 8px;`;
interface StarBtnProps { $active: boolean; $readOnly?: boolean; }
const StarButton = styled.div<StarBtnProps>`
  flex: 1;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? '#dde3ec' : '#e4e8ef')};
  background: #fff;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ $readOnly }) => ($readOnly ? '#e4e8ef' : '#d1d9e6')};
  }
`;
const LabelsRow = styled.div`display: flex; justify-content: space-between; margin-bottom: 2px;`;
const ScaleLabel = styled.div`
  color: #697386;
  font-size: 0.96rem;
  font-weight: 400;
  line-height: 1.25;
  font-family: ${modalFont};
`;
const Divider = styled.div`border-top: 1px dashed #e7ebf2; margin: 18px 0;`;
const ErrorText = styled.div`color: #FB7672; font-size: 0.8rem; margin-top: 4px;`;
const SectionLabel = styled.div`
  margin-bottom: 8px;
  color: #6f7787;
  font-size: 0.88rem;
  font-weight: 400;
  line-height: 1.35;
  font-family: ${modalFont};
`;
const WriteReviewLabel = styled.div`
  margin-bottom: 8px;
  color: #202939;
  font-size: 1rem;
  font-weight: 500;
  font-family: ${modalFont};
`;
const ReviewTextarea = styled.textarea`
  width: 100%;
  min-height: 84px;
  padding: 12px 16px;
  border: 1px solid #e4e8ef;
  border-radius: 10px;
  box-sizing: border-box;
  color: #202939;
  font-size: 0.92rem;
  line-height: 1.45;
  font-family: ${modalFont};
  resize: none;
  outline: none;
  background: #fff;

  &:hover {
    border-color: #d4dbe7;
  }

  &:focus {
    border-color: #d4dbe7;
  }

  &::placeholder {
    color: #9aa3b2;
    font-style: italic;
    font-size: 0.86rem;
    opacity: 1;
    font-family: ${modalFont};
  }
`;
const SkillCard = styled.div`
  display: flex; align-items: center; background: #fff; border-radius: 12px;
  padding: 14px 16px; margin-bottom: 8px; gap: 12px; border: 1px solid #edf0f5;
`;
interface IconCircleProps { $bg: string; $color: string; }
const IconCircle = styled.div<IconCircleProps>`
  width: 38px; height: 38px; border-radius: 10px; background: ${({ $bg }) => $bg};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  svg { font-size: 18px; color: ${({ $color }) => $color}; }
`;
const SkillName = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  color: #202939;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;
const SubmitContainer = styled.div`
  position: sticky;
  bottom: -20px;
  z-index: 3;
  margin: 14px -20px -20px;
  padding: 12px 20px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 22%, #fff 100%);
  border-top: 1px solid #edf0f5;
`;
const SubmitButton = styled(StyledButton)`
  margin: 0 !important;
  border-radius: 8px !important;
  padding: 0.72rem 0 !important;
  font-family: ${modalFont} !important;
  font-size: 0.95rem !important;
  font-weight: 500 !important;
  line-height: 1.2rem !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
`;
// T2-6191: de-emphasized "Skip for now" control. Replaces the full-width blue
// text button with a small light-grey chevron in the bottom-right so submitting
// is the obvious default while still allowing skip.
const SkipRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const SkipChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9aa3b2;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const skillConfig: { key: RatingFieldKey; label: string; icon: React.ReactNode; bg: string; color: string }[] = [
  { key: 'clinical',      label: 'Clinical Skills & Knowledge',    icon: <MenuBookOutlinedIcon />,            bg: '#e8f0fe', color: '#0B4598' },
  { key: 'professional',  label: 'Professionalism',                 icon: <BusinessCenterOutlinedIcon />,      bg: '#e6f9f0', color: '#13CA82' },
  { key: 'communication', label: 'Communication & Patient Care',    icon: <ChatBubbleOutlineOutlinedIcon />,   bg: '#fff6df', color: '#f5a623' },
  { key: 'time',          label: 'Time Management & Punctuality',   icon: <AccessTimeOutlinedIcon />,          bg: '#f3e8ff', color: '#9932CC' },
  { key: 'value',         label: 'Rate Satisfaction',               icon: <AttachMoneyOutlinedIcon />,         bg: '#ffebee', color: '#FB7672' },
];

const FeedbackPage: React.FC = () => {
  const history = useHistory();
  const locationState = (history.location.state || {}) as {
    job: JobProps; hygienist: HygienistProps; locationId: string | number;
    surveyJobs?: DentistSurveyJob[];
    currentSurveyIndex?: number; fromJobCompleted?: boolean;
    viewOnly?: boolean | number | string;
    editMode?: boolean | number | string;
    isEdit?: boolean | number | string;
    reviewAsk?: ReviewAskPayload;
  };
  const { job, hygienist, locationId, surveyJobs, currentSurveyIndex, fromJobCompleted } = locationState;
  const viewOnly = resolveFeedbackViewOnlyMode({
    state: locationState,
    search: history.location.search,
  });

  const [hasExistingFeedback, setHasExistingFeedback] = useState(!viewOnly && computeHasFeedback(job));
  const [isCheckingFeedback, setIsCheckingFeedback] = useState(true);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const starRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [collaborationPreference, setCollaborationPreference] = useState<CollabValue | null>(null);
  const [skillRatings, setSkillRatings] = useState<Record<RatingFieldKey, number | null>>({
    clinical: null, professional: null, communication: null, time: null, value: null,
  });
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [ratingTouched, setRatingTouched] = useState(false);
  const [showPositiveModal, setShowPositiveModal] = useState(false);
  const [showNegativeModal, setShowNegativeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showNegativeSuccessModal, setShowNegativeSuccessModal] = useState(false);
  const [selectedPositiveFeedback, setSelectedPositiveFeedback] = useState(`"Thanks so much, ${hygienist.firstName}!"`);
  const [negativeFeedback, setNegativeFeedback] = useState('');
  const [contactRequested, setContactRequested] = useState(false);
  const [contactName, setContactName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  // T2-7003: once the main feedback save succeeds, never reveal that submitted
  // form again while follow-ups, the review ask, or the home redirect finish.
  // The latch is reset only when this mounted flow advances to a different job.
  const [hasSubmittedCurrentSurvey, setHasSubmittedCurrentSurvey] = useState(false);
  // T2-6899: latch outside per-survey resetFormState — one ask per feedback run.
  // Ref keeps the value readable in the same tick as setState (exit-before-home).
  const latchedReviewAskRef = useRef<ReviewAskPayload | null>(locationState.reviewAsk || null);
  const [latchedReviewAsk, setLatchedReviewAsk] = useState<ReviewAskPayload | null>(locationState.reviewAsk || null);
  const [showReviewAskModal, setShowReviewAskModal] = useState(false);

  const returnToCompletedShift = () => {
    history.replace({
      pathname: 'jobCompleted',
      state: { jobId: job.id, jobCount: 1 },
    });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (viewOnly) {
          const existing = await api.getDentistFeedback(Number(job.id));
          if (!mounted) return;
          if (!existing) {
            setHydrateError('Unable to load existing feedback.');
            setIsCheckingFeedback(false);
            return;
          }
          const overall = existing.hygienistRating != null ? Number(existing.hygienistRating) : null;
          setOverallRating(Number.isFinite(overall as number) && (overall as number) > 0 ? overall : null);
          setCollaborationPreference((existing.collaborationPreference as CollabValue) || null);
          setSkillRatings({
            clinical: existing.clinical?.rating != null ? Number(existing.clinical.rating) : null,
            professional: existing.professional?.rating != null ? Number(existing.professional.rating) : null,
            communication: existing.communication?.rating != null ? Number(existing.communication.rating) : null,
            time: existing.time?.rating != null ? Number(existing.time.rating) : null,
            value: existing.value?.rating != null ? Number(existing.value.rating) : null,
          });
          setAdditionalFeedback(existing.additionalFeedback || '');
          setHasExistingFeedback(false);
          setIsCheckingFeedback(false);
          return;
        }

        const latest = await api.getJobDetails(job.id);
        if (!mounted) return;
        const already = computeHasFeedback(latest);
        setHasExistingFeedback(already);
        if (already) { history.push('loading'); exitReact('home'); return; }
        // T2-6961: first-time complete only within the 30-day Toronto window.
        if (!canCompleteDentistSurvey(latest as FeedbackAwareJob)) {
          setHydrateError('No feedback was submitted within 30 days of this shift being completed.');
          return;
        }
      } catch {
        if (viewOnly && mounted) {
          setHydrateError('Unable to load existing feedback. Please try again.');
        }
      }
      finally { if (mounted) setIsCheckingFeedback(false); }
    })();
    return () => { mounted = false; };
  }, [history, job.id, viewOnly]);

  const [saveFeedbackLoading, saveFeedbackError, submitFeedback] = useActionApi({
    apiMethod: api.saveFeedback,
    onSuccess: (res: any) => {
      setSubmitting(false);
      setHasSubmittedCurrentSurvey(true);
      finalizeFeedback();
      if (res?.reviewAsk?.solicitationId && !latchedReviewAskRef.current) {
        latchedReviewAskRef.current = res.reviewAsk as ReviewAskPayload;
        setLatchedReviewAsk(res.reviewAsk as ReviewAskPayload);
      }
      const followUpType = getDentistFeedbackFollowUpType(collaborationPreference);
      if (followUpType === 'positive') setShowPositiveModal(true);
      else if (followUpType === 'negative') setShowNegativeModal(true);
      else void handleRedirect();
    },
    onError: (error) => { setSubmitting(false); setApiError(error); },
  });
  const [positiveFeedbackLoading, positiveFeedbackError, submitPositiveFeedback] = useActionApi({
    apiMethod: api.positiveFeedback, onSuccess: () => setShowSuccessModal(true), onError: (e) => setApiError(e),
  });
  const [negativeFeedbackLoading, negativeFeedbackError, submitNegativeFeedback] = useActionApi({
    apiMethod: api.negativeFeedback, onSuccess: () => setShowNegativeSuccessModal(true), onError: (e) => setApiError(e),
  });
  const [skipSurveyLoading, , submitSkipSurvey] = useActionApi({
    apiMethod: api.skipFeedbackSurvey,
    onSuccess: () => {
      setSubmitting(false);
      void handleRedirect();
    },
    onError: () => {
      // T2-6569: Skip must dismiss the prompt even if the API returns 403/network
      // errors — never trap the DO behind FixedError on "Skip for now".
      setSubmitting(false);
      setApiError(null);
      void handleRedirect();
    },
  });

  const handleSubmit = () => {
    if (viewOnly) return;
    if (saveFeedbackLoading || skipSurveyLoading || submitting) return;
    if (hasExistingFeedback) { void handleRedirect(); return; }
    if (!overallRating) { setRatingTouched(true); return; }
    if (!canCompleteDentistSurvey(job as FeedbackAwareJob)) {
      setApiError(new ApiError(400, 'No feedback was submitted within 30 days of this shift being completed.'));
      return;
    }
    setSubmitting(true);
    submitFeedback({
      data: {
        jobId: job.id, hygienistId: hygienist.id, locationId,
        clinical: { rating: skillRatings.clinical, comment: '' },
        professional: { rating: skillRatings.professional, comment: '' },
        communication: { rating: skillRatings.communication, comment: '' },
        time: { rating: skillRatings.time, comment: '' },
        value: { rating: skillRatings.value, comment: '' },
        hygienistRating: overallRating, collaborationPreference,
        additionalFeedback, feedbackSubmitted: 1,
        ...(latchedReviewAskRef.current?.solicitationId
          ? { reviewSolicitationId: latchedReviewAskRef.current.solicitationId }
          : {}),
      },
    });
  };

  const loadNextEligibleSurveyFromServer = async () => {
    try {
      const latestJobs = await api.getDentistJobs();
      const nextSurveyJobs = buildDentistSurveyQueue(Array.isArray(latestJobs) ? latestJobs : []);
      if (!nextSurveyJobs.length) return false;

      const next = nextSurveyJobs[0];
      setHasExistingFeedback(false);
      setApiError(null);
      setShouldRedirect(false);
      history.replace('feedbackPage', {
        ...next,
        surveyJobs: nextSurveyJobs,
        currentSurveyIndex: 0,
        reviewAsk: latchedReviewAskRef.current || undefined,
      });
      resetFormState(next.hygienist.firstName);
      return true;
    } catch {
      return false;
    }
  };

  const handleRedirect = async () => {
    if (viewOnly) {
      returnToCompletedShift();
      return;
    }
    if (surveyJobs && currentSurveyIndex !== undefined && currentSurveyIndex + 1 < surveyJobs.length) {
      const next = surveyJobs[currentSurveyIndex + 1];
      history.replace('feedbackPage', {
        ...next,
        surveyJobs,
        currentSurveyIndex: currentSurveyIndex + 1,
        // Cordova can remount this route between queued surveys. Carry the
        // first ask through navigation state so the next submit reuses it.
        reviewAsk: latchedReviewAskRef.current || undefined,
      });
      resetFormState(next.hygienist.firstName);
      return;
    }
    if (fromJobCompleted) {
      const redirectedToNext = await loadNextEligibleSurveyFromServer();
      if (redirectedToNext) return;
    }
    // T2-6899: show review ask once at end of queue / follow-ups, before home.
    if (latchedReviewAskRef.current && !showReviewAskModal) {
      setShowReviewAskModal(true);
      return;
    }
    history.push('loading');
    exitReact('home');
  };

  const handleReviewAskDone = () => {
    setShowReviewAskModal(false);
    latchedReviewAskRef.current = null;
    setLatchedReviewAsk(null);
    history.push('loading');
    exitReact('home');
  };

  const resetFormState = (fn: string) => {
    setOverallRating(null); setCollaborationPreference(null); setRatingTouched(false);
    setSkillRatings({ clinical: null, professional: null, communication: null, time: null, value: null });
    setAdditionalFeedback(''); setSelectedPositiveFeedback(`"Thanks so much, ${fn}!"`);
    setNegativeFeedback(''); setContactRequested(false); setContactName('');
    setShouldRedirect(false); setApiError(null); setHasExistingFeedback(false);
    setHasSubmittedCurrentSurvey(false);
  };

  const finalizeFeedback = () => {
    setShouldRedirect(true);
  };

  const handleSkipForNow = () => {
    if (viewOnly) return;
    if (saveFeedbackLoading || skipSurveyLoading || submitting) return;
    setSubmitting(true);
    submitSkipSurvey({ jobId: Number(job.id) });
  };

  const handlePositiveFeedbackSubmit = (pf: string) => {
    setShowPositiveModal(false);
    if (pf !== 'not') submitPositiveFeedback({ job, feedback: pf }).then(() => setShowSuccessModal(true)).catch(() => void handleRedirect());
    else void handleRedirect();
  };
  const handleNegativeFeedbackSubmit = () => {
    setShowNegativeModal(false);
    submitNegativeFeedback({ job, negativeFeedback, contact: contactRequested, name: contactName })
      .then(() => setShowNegativeSuccessModal(true)).catch(() => void handleRedirect());
  };

  /** Main feedback is already saved; Go Back skips the optional follow-up and leaves the flow (same outcome as neutral submit / "Don't send a note"). */
  const handleFollowUpGoBack = () => {
    setShowPositiveModal(false);
    setShowNegativeModal(false);
    void handleRedirect();
  };

  const isSubmitDisabled = !overallRating || saveFeedbackLoading || skipSurveyLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting;
  // Only one MUI Modal open at a time: a stacked main + follow-up modal breaks taps (e.g. "Go Back") on mobile/Cordova.
  const anyFollowUpModalOpen =
    showPositiveModal || showNegativeModal || showSuccessModal || showNegativeSuccessModal;
  const showForm = (!hasExistingFeedback || viewOnly)
    && !hasSubmittedCurrentSurvey
    && !isCheckingFeedback
    && !anyFollowUpModalOpen
    && !hydrateError;
  const fixedError = saveFeedbackError || positiveFeedbackError || negativeFeedbackError || apiError;

  useEffect(() => {
    setChatbaseUiHidden(showForm);
    return () => setChatbaseUiHidden(false);
  }, [showForm]);

  return (
    <>
      <WiderModalStyle />
      <SimpleModal isOpen={showForm} className="feedback-do-modal" disableBackdropClose disableEscapeKeyDown>
        <ModalContent className="no-fastclick">
          <ModalHeaderBand>
            <ModalHeaderInner>
              <HeaderLeft>
                <CheckCircleOutlineIcon sx={{ color: '#8d94a3', fontSize: 20 }} />
                <HeaderTitle>Feedback</HeaderTitle>
              </HeaderLeft>
            </ModalHeaderInner>
          </ModalHeaderBand>

          <ModalBody>
          <QuestionTitle>
            How was your shift on {moment(job.startDate).format('MMMM D, YYYY')} with {hygienist.firstName}?
          </QuestionTitle>

          <StarRow role="radiogroup" aria-label="Overall rating" aria-disabled={viewOnly || undefined}>
            {[1, 2, 3, 4, 5].map((s, i) => {
              const active = overallRating !== null && s <= overallRating;
              const selected = overallRating === s;
              return (
                <StarButton
                  key={s}
                  ref={(el: HTMLDivElement | null) => { starRefs.current[i] = el; }}
                  $active={active}
                  $readOnly={viewOnly}
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={viewOnly || undefined}
                  aria-label={`${s} star${s > 1 ? 's' : ''}`}
                  tabIndex={viewOnly ? -1 : (selected || (!overallRating && s === 1) ? 0 : -1)}
                  onClick={viewOnly ? undefined : () => { setOverallRating(s); setRatingTouched(true); }}
                  onKeyDown={viewOnly ? undefined : (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOverallRating(s); setRatingTouched(true); }
                    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                      e.preventDefault(); const next = Math.min(4, i + 1); setOverallRating(next + 1); setRatingTouched(true); starRefs.current[next]?.focus();
                    }
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                      e.preventDefault(); const prev = Math.max(0, i - 1); setOverallRating(prev + 1); setRatingTouched(true); starRefs.current[prev]?.focus();
                    }
                  }}
                >
                  <StarRoundedIcon sx={{ fontSize: 34, color: active ? '#f5a623' : '#e6ebf2' }} />
                </StarButton>
              );
            })}
          </StarRow>
          <LabelsRow>
            <ScaleLabel>Poor</ScaleLabel>
            <ScaleLabel>Great</ScaleLabel>
          </LabelsRow>
          {!viewOnly && !overallRating && <ErrorText>Please select a rating</ErrorText>}

          <Divider />

          <FutureCollaboration
            value={collaborationPreference}
            firstName={hygienist.firstName}
            onChange={setCollaborationPreference}
            readOnly={viewOnly}
          />

          <Divider />

          <WriteReviewLabel>Write Review</WriteReviewLabel>
          <ReviewTextarea
            rows={4}
            placeholder={viewOnly ? '' : `Write notes here, this is internal information only and ${hygienist.firstName} won't see it`}
            value={additionalFeedback}
            onChange={viewOnly ? undefined : (e) => setAdditionalFeedback(e.target.value)}
            readOnly={viewOnly}
          />

          <Divider />

          <SectionLabel>Additional Feedback</SectionLabel>
          {skillConfig.map(({ key, label, icon, bg, color }) => (
            <SkillCard key={key}>
              <IconCircle $bg={bg} $color={color}>{icon}</IconCircle>
              <SkillName>{label}</SkillName>
              <Rating
                size="medium"
                value={skillRatings[key]}
                readOnly={viewOnly}
                icon={<StarRoundedIcon fontSize="inherit" />}
                emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                onChange={viewOnly ? undefined : (_, v) => setSkillRatings(prev => ({ ...prev, [key]: v }))}
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  fontSize: '1.55rem',
                  gap: '2px',
                  '& .MuiRating-iconFilled': { color: '#f5a623' },
                  '& .MuiRating-iconEmpty': { color: '#e6ebf2' },
                }}
              />
            </SkillCard>
          ))}

          <SubmitContainer>
            {viewOnly ? (
              <SubmitButton
                id="dentist-done-feedback-view-button"
                data-testid="dentist-done-feedback-view-button"
                aria-label="Done"
                content-desc="dentist-done-feedback-view-button"
                variant="contained"
                className="btn btn-primary"
                fullWidth
                onClick={() => void handleRedirect()}
              >
                Done
              </SubmitButton>
            ) : (
              <>
                <SubmitButton
                  id="dentist-feedback-submit-button"
                  data-testid="dentist-feedback-submit-button"
                  aria-label="Submit"
                  variant="contained"
                  className="btn btn-primary"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                >
                  {saveFeedbackLoading || submitting ? 'Submitting...' : 'Submit'}
                </SubmitButton>
                <SkipRow>
                  <SkipChevronButton
                    type="button"
                    onClick={handleSkipForNow}
                    disabled={saveFeedbackLoading || skipSurveyLoading || submitting}
                    aria-label="Skip for now"
                    title="Skip for now"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </SkipChevronButton>
                </SkipRow>
              </>
            )}
          </SubmitContainer>
          </ModalBody>
        </ModalContent>
      </SimpleModal>

      {hydrateError && (
        <FixedError
          error={new ApiError(400, hydrateError)}
          onDismiss={viewOnly ? () => { setHydrateError(null); returnToCompletedShift(); } : undefined}
        />
      )}

      <PositiveFeedbackModal isOpen={showPositiveModal} onClose={handleFollowUpGoBack}
        hygienistFirstName={hygienist.firstName} selectedFeedback={selectedPositiveFeedback}
        onFeedbackChange={setSelectedPositiveFeedback} onSubmit={handlePositiveFeedbackSubmit} />
      <PositiveSuccessModal isOpen={showSuccessModal}
        onClose={() => { setShowSuccessModal(false); if (shouldRedirect) void handleRedirect(); }}
        hygienistFirstName={hygienist.firstName} />
      <NegativeFeedbackModal isOpen={showNegativeModal} onClose={handleFollowUpGoBack}
        hygienistFirstName={hygienist.firstName} negativeFeedback={negativeFeedback}
        contactRequested={contactRequested} contactName={contactName}
        onFeedbackChange={setNegativeFeedback} onContactRequestedChange={setContactRequested}
        onContactNameChange={setContactName} onSubmit={handleNegativeFeedbackSubmit} />
      <NegativeSuccessModal isOpen={showNegativeSuccessModal}
        onClose={() => { setShowNegativeSuccessModal(false); if (shouldRedirect) void handleRedirect(); }}
        hygienistFirstName={hygienist.firstName} />
      <ReviewAskModal
        open={showReviewAskModal}
        reviewAsk={latchedReviewAsk}
        role="dentist"
        onDone={handleReviewAskDone}
      />
      <FixedLoader loading={isCheckingFeedback || saveFeedbackLoading || skipSurveyLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting} />
      <FixedError error={fixedError} />
    </>
  );
};

export default FeedbackPage;
